import React, { useState, useRef } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRotateLeft,
  faRotateRight,
  faMagnifyingGlassPlus,
  faMagnifyingGlassMinus,
} from "@fortawesome/free-solid-svg-icons";
import "react-image-crop/dist/ReactCrop.css";

function centerAspectCrop(mediaWidth, mediaHeight) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      mediaWidth / mediaHeight,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

const ImageCropper = ({ imageFile, onCropComplete, onCancel }) => {
  const [crop, setCrop] = useState();
  const [imageSrc, setImageSrc] = useState(null);
  const imgRef = useRef(null);
  const [error, setError] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Load image when component mounts
  React.useEffect(() => {
    if (!imageFile) {
      setError("No image file provided");
      return;
    }

    console.log("Loading image file:", imageFile.name, imageFile.type);

    const reader = new FileReader();
    reader.onload = () => {
      console.log("Image file loaded successfully");
      setImageSrc(reader.result);
    };
    reader.onerror = () => {
      console.error("Error reading file:", reader.error);
      setError("Failed to load image file");
    };
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  function onImageLoad(e) {
    const { width, height } = e.currentTarget;
    const crop = centerAspectCrop(width, height);
    setCrop(crop);
  }

  const handleRotate = (direction) => {
    setRotate((prev) => (prev + (direction === "left" ? -90 : 90)) % 360);
  };

  const handleZoom = (direction) => {
    setScale((prev) => {
      const newScale = direction === "in" ? prev + 0.1 : prev - 0.1;
      return Math.max(0.5, Math.min(3, newScale)); // Limit scale between 0.5 and 3
    });
  };

  const getCroppedImg = async () => {
    if (!imgRef.current || !completedCrop?.width || !completedCrop?.height) {
      console.error("Missing required data for crop");
      return;
    }

    setIsProcessing(true);

    try {
      const canvas = document.createElement("canvas");
      const image = imgRef.current;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      // Adjust dimensions for rotation
      const isRotated90or270 = rotate % 180 !== 0;
      const canvasWidth = isRotated90or270
        ? completedCrop.height * scaleY
        : completedCrop.width * scaleX;
      const canvasHeight = isRotated90or270
        ? completedCrop.width * scaleX
        : completedCrop.height * scaleY;

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error("Failed to get canvas context");
      }

      ctx.imageSmoothingQuality = "high";

      // Move to center and rotate
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotate * Math.PI) / 180);
      ctx.scale(scale, scale);

      // Draw the image centered
      ctx.drawImage(
        image,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        -canvasWidth / 2,
        -canvasHeight / 2,
        canvasWidth,
        canvasHeight
      );

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error("Canvas is empty");
            setError("Failed to create cropped image");
            setIsProcessing(false);
            return;
          }
          // Create a new file from the blob
          const croppedFile = new File([blob], imageFile.name, {
            type: imageFile.type,
          });
          console.log("Cropped image created successfully");
          onCropComplete(croppedFile);
        },
        imageFile.type,
        1
      );
    } catch (err) {
      console.error("Error during crop:", err);
      setError("Failed to crop image");
      setIsProcessing(false);
    }
  };

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 dark:bg-gray-800">
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-md dark:bg-gray-800">
          <h3 className="text-xl font-bold mb-4 text-red-600 dark:text-red-400">
            Error
          </h3>
          <p className="mb-4">{error}</p>
          <div className="flex justify-end">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!imageSrc) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 dark:bg-gray-800">
        <div className="bg-white p-6 rounded-lg shadow-xl dark:bg-gray-800">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto dark:border-primary-600"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Loading image...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-4xl w-full mx-4 dark:bg-gray-800">
        <h3 className="text-xl font-bold mb-4 dark:text-gray-100">
          Crop Image
        </h3>

        {/* Controls */}
        <div className="flex justify-center gap-4 mb-4">
          <div className="flex gap-2">
            <button
              onClick={() => handleRotate("left")}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Rotate Left"
            >
              <FontAwesomeIcon icon={faRotateLeft} />
            </button>
            <button
              onClick={() => handleRotate("right")}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Rotate Right"
            >
              <FontAwesomeIcon icon={faRotateRight} />
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleZoom("out")}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Zoom Out"
              disabled={scale <= 0.5}
            >
              <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
            </button>
            <button
              onClick={() => handleZoom("in")}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Zoom In"
              disabled={scale >= 3}
            >
              <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
            </button>
          </div>
        </div>

        <div className="mb-4 overflow-auto max-h-[60vh]">
          {Boolean(imageSrc) && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              className="max-w-full dark:bg-gray-800"
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={imageSrc}
                onLoad={onImageLoad}
                style={{
                  maxWidth: "100%",
                  transform: `scale(${scale}) rotate(${rotate}deg)`,
                  transition: "transform 0.2s ease",
                }}
              />
            </ReactCrop>
          )}
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onCropComplete(imageFile);
              setIsProcessing(true);
            }}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors relative dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <span className="opacity-0">Skip Crop</span>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white dark:border-gray-300"></div>
                </div>
              </>
            ) : (
              "Skip Crop"
            )}
          </button>
          <button
            onClick={getCroppedImg}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors relative dark:bg-primary-600 dark:text-white dark:hover:bg-primary-700"
            disabled={
              !completedCrop?.width || !completedCrop?.height || isProcessing
            }
          >
            {isProcessing ? (
              <>
                <span className="opacity-0">Crop</span>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white dark:border-gray-300"></div>
                </div>
              </>
            ) : (
              "Crop"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
